/**
 * Service Worker Bridge — MessageChannel for SW ↔ Main Thread Communication
 *
 * Establishes a MessageChannel port between the main thread and the Service
 * Worker to allow bidirectional communication for Background Sync operations.
 *
 * The Service Worker triggers a `runDrain` request when the browser fires its
 * `sync` event. The main thread handles the drain through the already-running
 * `SyncWorker` singleton (reusing `httpClient` with baseURL + bearer token +
 * method mapping + retry semantics). After the drain completes, the bridge
 * acks back with `drainComplete` (success) or `drainError` (with reason).
 */

import { getSyncWorker } from '@/shared/sync/syncWorker.singleton';

type SwRequest = { type: 'runDrain' };

type SwResponse =
  | { type: 'drainComplete' }
  | { type: 'drainError'; error: string };

let bridgePort: MessagePort | null = null;

/**
 * Initialize the MessageChannel bridge with the active Service Worker.
 * Call this once during app initialization after the SW is registered.
 */
export async function initServiceWorkerBridge(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW Bridge] Service Worker not supported in this browser');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration.active) {
      console.warn('[SW Bridge] No active Service Worker found');
      return;
    }

    // Create MessageChannel
    const channel = new MessageChannel();
    bridgePort = channel.port1;

    // Listen to messages from the Service Worker
    bridgePort.onmessage = (event: MessageEvent<SwRequest>) => {
      handleSwRequest(event.data);
    };

    // Send port2 to the Service Worker
    registration.active.postMessage({ type: 'PORT_INIT' }, [channel.port2]);

    console.log('[SW Bridge] MessageChannel established with Service Worker');
  } catch (error) {
    console.error('[SW Bridge] Failed to initialize:', error);
  }
}

/**
 * Handle incoming requests from the Service Worker.
 */
async function handleSwRequest(request: SwRequest): Promise<void> {
  if (!bridgePort) {
    console.error('[SW Bridge] Cannot handle request: no bridge port');
    return;
  }

  try {
    switch (request.type) {
      case 'runDrain': {
        console.log('[SW Bridge] Drain requested by Service Worker');
        const worker = getSyncWorker();
        if (!worker) {
          const response: SwResponse = {
            type: 'drainError',
            error: 'SyncWorker singleton not initialized yet',
          };
          bridgePort.postMessage(response);
          break;
        }
        try {
          await worker.syncNow();
          const response: SwResponse = { type: 'drainComplete' };
          bridgePort.postMessage(response);
          console.log('[SW Bridge] Drain completed; ack sent to SW');
        } catch (err) {
          const response: SwResponse = {
            type: 'drainError',
            error: err instanceof Error ? err.message : String(err),
          };
          bridgePort.postMessage(response);
          console.error('[SW Bridge] Drain failed:', err);
        }
        break;
      }

      default:
        console.warn('[SW Bridge] Unknown request type:', request);
    }
  } catch (error) {
    console.error('[SW Bridge] Error handling request:', error);
  }
}

/**
 * Get the current bridge port (for testing or debugging).
 */
export function getBridgePort(): MessagePort | null {
  return bridgePort;
}
